import { action, asyncAction } from '@play-co/replicant';
import createActions from 'src/replicant/config/actions/createActions';
import type { AsyncActionAPI } from 'src/replicant/utils/types';
import type { State } from '../State';
import {
  CardId,
  CardsRewardId,
  cardsInviteRequestGrantedCardCount,
} from './cards.ruleset';
import {
  cardsAddCardReceived,
  cardsAddCounteredShareTradeAsRecipient,
  cardsAddCard,
  cardsCancelTrade,
  cardsCleanupExpiredTradesWithAnalytics,
  cardsClaimRewards,
  cardsClearNewlyAdded,
  cardsCollectAllCardsReceived,
  cardsCompleteTradeAsProposer,
  cardsCounterTrade,
  cardsCreateTrade,
  cardsSendCard,
  cardsSetSeenAt,
} from './cards.modifiers';
import {
  cardsCanReceiveCard,
  cardsCanSendCard,
  cardsCreateShareTradeId,
  cardsGetOwnedCardCount,
  cardsGetPendingTrade,
  cardsGetRewardsStatus,
  cardsHasOwnedCards,
  cardsPickInviteGrantCard,
  cardsHasRecipientCompletedTrade,
  cardsIsCardValid,
  cardsIsValidPendingTrade,
  cardsIsEnabled,
  cardsValidateDonateShareLink,
  cardsValidateDonateShareOther,
  cardsValidateDonateShareSelf,
  cardsValidateInviteRequestLink,
  cardsValidateInviteRequestOther,
  cardsValidateInviteRequestRedemption,
  cardsValidateTradeLink,
  cardsValidateTradeOther,
  cardsValidateTradeSelf,
} from './cards.getters';
import type {
  CardsDonateShareArgs,
  CardsDonateShareResult,
  CardsInviteRequestArgs,
  CardsInviteRequestPreviewResult,
  CardsInviteRequestRedemptionResult,
  CardsShareTradePreviewResult,
  CardsShareTradeResult,
  CardsTradeArgs,
} from './cards.types';
import { grantMatch2Rewards } from '../match2/match2.modifiers';
import { processMatch2Rewards } from '../match2/rewards/match2-rewards.getters';
import type {
  Match2Reward,
  PredefinedRewardId,
} from '../match2/rewards/match2-rewards.ruleset';
import { openCardPack } from '../cardpack/cardpack.modifiers';
import { CardPackID } from '../cardpack/cardpack.ruleset';
import { addFriend } from '../friends/friends.modifiers';
import { getBuruFriendsCount, isBuruFriend } from '../friends/friends.getters';
import {
  cardsGetAcceptAnalyticsEvent,
  cardsGetAnalyticsInventory,
  cardsGetCompletionAnalyticsEvents,
  cardsGetRequestAnalyticsEvent,
  cardsGetSendAnalyticsEvent,
  cardsGetTradeConfirmAnalyticsEvent,
  cardsGetTradeRejectAnalyticsEvent,
  cardsGetTradeSendAnalyticsEvent,
  cardsRequestSubFeatureByMethod,
  type CardAcceptSource,
  type CardsDeliveryMethod,
} from './cards.analytics';
import { sendAnalyticsEvents, type AnalyticsEvent } from '../analytics';

/** Legacy reward-wrapper shape used by JSON-backed Cards configuration. */
type CardsConfiguredRewardWrapper = {
  type: 'wrapper';
  subType: PredefinedRewardId;
  amount: number;
};

/**
 * Convert legacy Cards wrapper configuration into the Match2 reward contract.
 *
 * @param rewards Configured Cards rewards to normalize.
 * @returns Rewards using the wrapper shape expected by Match2 processing.
 */
function cardsNormalizeRewardWrappers(rewards: Match2Reward[]): Match2Reward[] {
  // Cards data predates the Match2 wrapper contract and is cast at its boundary.
  const configuredRewards = rewards as Array<
    Match2Reward | CardsConfiguredRewardWrapper
  >;

  // Preserve standard rewards and adapt only legacy Cards wrappers.
  return configuredRewards.map((reward): Match2Reward => {
    // Non-wrapper rewards already use the Match2 reward contract.
    if (reward.type !== 'wrapper') return reward;

    // Match2 wrappers already identify their predefined reward with id or ids.
    if (!('subType' in reward)) return reward;

    // Convert the legacy Cards subtype into the predefined reward id.
    return {
      type: 'wrapper',
      id: reward.subType,
      amount: reward.amount,
    };
  });
}

async function cardsFetchUserState(
  userId: string,
  api: AsyncActionAPI,
): Promise<State | undefined> {
  return (await api.fetchStates([userId]))?.[userId]?.state;
}

/**
 * Replicant action handlers for Cards collection inventory, rewards, invite
 * requests, and asynchronous trades.
 */
export const cardsActions = createActions({
  /**
   * Send one duplicate card to another user.
   * The sender must keep at least one copy. On success, the card is removed
   * from sender inventory, a receive message is posted, and the receive OA is
   * scheduled.
   *
   * @throws When sending to self, sending an unowned card, sending the last
   * copy, or exceeding modifier-level send limits.
   */
  cardsSendCard: asyncAction(
    async (
      state,
      args: {
        cardId: CardId;
        userId: string;
        source?: CardAcceptSource;
        sendMethod?: CardsDeliveryMethod;
      },
      api,
    ) => {
      if (!cardsIsCardValid(args.cardId)) {
        throw new Error(`Unknown card ${args.cardId}.`);
      }

      if (args.userId === state.id) {
        throw new Error('Cannot send a card to yourself');
      }

      // Check if user has the card to send - must have at least 2 of the same card
      const owned = cardsGetOwnedCardCount(state, args.cardId, api.date.now());

      // Check if the card is owned
      if (owned <= 0) {
        throw new Error('Cannot send a card that is not owned');
      }

      // Owned amount is at least one here
      if (owned === 1) {
        throw new Error('Cannot send the last card of a kind');
      }

      // Read recipient inventory without changing send behavior if unavailable.
      const recipientState = await cardsFetchUserState(args.userId, api);
      const recipientInventoryBefore = recipientState
        ? cardsGetAnalyticsInventory(recipientState)
        : undefined;

      // Remove the card from the owned inventory
      cardsSendCard(state, {
        cardId: args.cardId,
        now: api.date.now(),
      });

      // Send the card to the receiver
      api.postMessage.cardsSendCard(args.userId, {
        cardId: args.cardId,
      });

      // Send OA.
      api.scheduledActions.schedule.sendCardReceivedOA({
        args: { cardId: args.cardId },
        delayInMS: 0,
        targetUserId: args.userId,
        notificationId: `cardsSendCard_${args.cardId}`,
      });

      // Emit sender analytics from the authoritative post-send state.
      const sendMethod = args.sendMethod ?? 'request';
      const events: AnalyticsEvent[] = [
        cardsGetSendAnalyticsEvent(state, {
          cardId: args.cardId,
          friendId: args.userId,
          sendMethod,
        }),
      ];

      // Emit the receiver pair cross-user when their state was available.
      if (
        recipientState &&
        recipientInventoryBefore &&
        cardsCanReceiveCard(recipientState, api.date.now())
      ) {
        const recipientInventoryAfter = cardsGetAnalyticsInventory(
          recipientState,
          { [args.cardId]: 1 },
        );
        events.push(
          cardsGetAcceptAnalyticsEvent(recipientInventoryAfter, {
            cardId: args.cardId,
            friendId: state.id,
            source: args.source ?? 'donate',
            userId: args.userId,
          }),
          ...cardsGetCompletionAnalyticsEvents(
            recipientInventoryBefore,
            recipientInventoryAfter,
            args.userId,
          ),
        );
      }

      sendAnalyticsEvents(api, events);
    },
  ),
  /**
   * Collect every received-card ledger entry into owned inventory.
   * Received cards are only added to owned cards when this action runs.
   */
  cardsCollectAllCardsReceived: action((state, args: void, api) => {
    cardsCollectAllCardsReceived(state, api.date.now());
  }),
  /**
   * Remove expired trades and record that the user viewed the cards album.
   */
  cardsUpdateSeenAt: action((state, args: void, api) => {
    // Use one timestamp so cleanup and the seen marker agree.
    const now = api.date.now();

    // Release cards escrowed by trades whose response window has elapsed.
    cardsCleanupExpiredTradesWithAnalytics(state, now, api);

    // Record the latest Cards visit after cleanup is complete.
    cardsSetSeenAt(state, now);
  }),
  /**
   * Clear newly-added markers for the provided card ids.
   */
  cardsClearNewlyAdded: action((state, args: { ids: CardId[] }) => {
    cardsClearNewlyAdded(state, args.ids);
  }),
  /**
   * Claim an available collection or full-album reward.
   * Claimed rewards are granted immediately through the Match2 rewards path.
   *
   * @returns The rewards granted for the claimed reward id.
   * @throws When the reward is not available or no rewards can be claimed.
   */
  cardsClaimRewards: action((state, args: { rewardId: CardsRewardId }, api) => {
    const status = cardsGetRewardsStatus(state, args.rewardId);
    if (status !== 'available') {
      throw new Error(
        `Cannot claim rewards for set ${args.rewardId} - status: ${status}`,
      );
    }

    // If user can claim, this method should return the rewards
    const rewards = cardsClaimRewards(state, args.rewardId);
    if (!rewards) {
      throw new Error('No rewards to claim');
    }

    // Convert legacy Cards wrappers into the Match2 reward contract.
    const normalizedRewards = cardsNormalizeRewardWrappers(rewards);

    // Expand nested rewards before passing them to the inventory grant path.
    const processedRewards = processMatch2Rewards(
      normalizedRewards,
      api.date.now(),
    );

    // Grant every expanded reward to the user.
    grantMatch2Rewards(
      state,
      processedRewards,
      {
        feature: 'cards',
        $subFeature: args.rewardId,
      },
      api,
    );

    // Return the configured rewards so the UI keeps its grouped presentation.
    return rewards;
  }),
  /**
   * Open a card pack from the user's inventory.
   *
   * @returns The cards granted by the opened pack.
   */
  cardsOpenPack: action((state, args: { id: CardPackID }, api) => {
    return openCardPack(state, args.id, api.date.now());
  }),

  /**
   * Send an OA message to a user requesting a card.
   * The user may or may not still have that card.
   *
   * @returns True when the request notification was scheduled.
   */
  cardsRequestCardFromUser: action(
    (state, args: { cardId: CardId; userId: string }, api): boolean => {
      const now = api.date.now();

      if (!cardsIsCardValid(args.cardId)) return false;
      if (args.userId === state.id) return false;
      if (!cardsIsEnabled(state)) return false;
      if (!cardsCanReceiveCard(state, now)) return false;
      if (cardsGetOwnedCardCount(state, args.cardId, now) > 0) return false;

      api.scheduledActions.schedule.sendCardRequestOA({
        args: {
          cardId: args.cardId,
          requesterId: state.id,
          requestedAt: now,
        },
        delayInMS: 0,
        targetUserId: args.userId,
        notificationId: `cardsRequestCard_${state.id}_${args.userId}_${args.cardId}`,
      });

      // Count direct OA delivery as the canonical in-game request method.
      sendAnalyticsEvents(api, [
        cardsGetRequestAnalyticsEvent(state, args.cardId, 'request'),
      ]);

      return true;
    },
  ),

  /**
   * Preview whether a card request invite can result in a card send without
   * mutating cards, friends, or feature-flag state.
   *
   * @returns A sendable result, optionally with the card minted by redemption,
   * or the reason no card can be sent.
   */
  cardsPreviewInviteRequest: asyncAction(
    async (
      state,
      args: CardsInviteRequestArgs,
      api,
    ): Promise<CardsInviteRequestPreviewResult> => {
      const now = api.date.now();

      // Request links must identify another player and a valid card.
      const linkValidation = cardsValidateInviteRequestLink(state, args);
      if (!linkValidation.valid) {
        return { canSend: false, reason: linkValidation.reason };
      }

      // Invitees with owned cards need a duplicate of the requested card.
      const hasOwnedCards = cardsHasOwnedCards(state);
      const ownedCardCount = cardsGetOwnedCardCount(state, args.cardId, now);
      if (hasOwnedCards && ownedCardCount < 2) {
        return { canSend: false, reason: 'not_enough_cards' };
      }

      // Invitees with owned cards must also have remaining send capacity.
      if (hasOwnedCards && !cardsCanSendCard(state, now)) {
        return { canSend: false, reason: 'send_limit_reached' };
      }

      // Existing-card sends still need a requester who can receive the card.
      if (hasOwnedCards) {
        const inviterState = await cardsFetchUserState(args.inviterId, api);
        if (!inviterState) {
          return { canSend: false, reason: 'requester_unavailable' };
        }
        if (!cardsCanReceiveCard(inviterState, now)) {
          return {
            canSend: false,
            reason: 'requester_receive_limit_reached',
          };
        }
        return { canSend: true };
      }

      // Cardless players must pass the redemption-specific checks.
      const redemptionValidation = cardsValidateInviteRequestRedemption(
        state,
        args,
        now,
      );
      if (!redemptionValidation.valid) {
        return { canSend: false, reason: redemptionValidation.reason };
      }

      // Redemption may select a mintable fallback for the requested card.
      const redeemCardId = cardsPickInviteGrantCard(state, args);
      if (!redeemCardId) {
        return { canSend: false, reason: 'request_unavailable' };
      }

      // Requesters must still exist when redemption is previewed.
      const inviterState = await cardsFetchUserState(args.inviterId, api);
      if (!inviterState) {
        return { canSend: false, reason: 'requester_unavailable' };
      }

      // Both players must be eligible for the friendship created by redemption.
      if (!cardsCanReceiveCard(inviterState, now)) {
        return {
          canSend: false,
          reason: 'requester_receive_limit_reached',
        };
      }
      const otherValidation = cardsValidateInviteRequestOther(
        state,
        inviterState,
        args,
      );
      if (!otherValidation.valid) {
        return { canSend: false, reason: otherValidation.reason };
      }

      // The request can proceed through the mint-and-send redemption route.
      return {
        canSend: true,
        redeemCardId,
      };
    },
  ),

  /**
   * Redeem a card request link for an invitee.
   *
   * This mints enough copies for the invitee to send one card and keep one,
   * then connects the invitee with the requester without creating generic
   * referral reward state.
   * The requester is sent a card message and scheduled card-received OA, and
   * receives a reciprocal friend message when needed.
   *
   * @returns The granted card id, or a rejected result with a reason.
   */
  cardsRedeemInviteRequest: asyncAction(
    async (
      state,
      args: CardsInviteRequestArgs,
      api,
    ): Promise<CardsInviteRequestRedemptionResult> => {
      const now = api.date.now();
      const linkValidation = cardsValidateInviteRequestLink(state, args);
      if (!linkValidation.valid) {
        return { granted: false, reason: linkValidation.reason };
      }

      const redemptionValidation = cardsValidateInviteRequestRedemption(
        state,
        args,
        now,
      );
      if (!redemptionValidation.valid) {
        return { granted: false, reason: redemptionValidation.reason };
      }

      const grantedCardId = cardsPickInviteGrantCard(state, args);
      if (!grantedCardId) {
        return { granted: false, reason: 'request_unavailable' };
      }

      // Capture the invitee-side friendship before adding the requester.
      const isAlreadyFriend = isBuruFriend(state, args.inviterId);

      // Requesters must exist before cards or friendship state are changed.
      const inviterState = await cardsFetchUserState(args.inviterId, api);
      if (!inviterState) {
        return { granted: false, reason: 'requester_unavailable' };
      }

      // Capture the requester-side friendship before posting reciprocal changes.
      const inviterAlreadyFriend = isBuruFriend(inviterState, state.id);

      // Requesters must have capacity for the card returned through the link.
      if (!cardsCanReceiveCard(inviterState, now)) {
        return {
          granted: false,
          reason: 'requester_receive_limit_reached',
        };
      }

      // Both players must remain eligible for the friendship created by redemption.
      const otherValidation = cardsValidateInviteRequestOther(
        state,
        inviterState,
        args,
      );
      if (!otherValidation.valid) {
        return { granted: false, reason: otherValidation.reason };
      }

      // Capture effective inventories before the minted invite cards are added.
      const inviteeInventoryBefore = cardsGetAnalyticsInventory(state);
      const inviterInventoryBefore = cardsGetAnalyticsInventory(inviterState);
      const events: AnalyticsEvent[] = [];

      for (let i = 0; i < cardsInviteRequestGrantedCardCount; i++) {
        cardsAddCard(state, {
          cardId: grantedCardId,
          now,
        });

        // Invite grants emit CardAccept once for every minted card.
        events.push(
          cardsGetAcceptAnalyticsEvent(cardsGetAnalyticsInventory(state), {
            cardId: grantedCardId,
            friendId: args.inviterId,
            source: 'invite',
          }),
        );
      }

      cardsSendCard(state, {
        cardId: grantedCardId,
        now,
      });

      const requestMethod = args.requestMethod ?? 'native_share';
      events.push(
        cardsGetSendAnalyticsEvent(state, {
          cardId: grantedCardId,
          friendId: args.inviterId,
          sendMethod: requestMethod,
        }),
      );

      // Attribute the returned request card to the original requester.
      const inviterInventoryAfter = cardsGetAnalyticsInventory(inviterState, {
        [grantedCardId]: 1,
      });
      events.push(
        cardsGetAcceptAnalyticsEvent(inviterInventoryAfter, {
          cardId: grantedCardId,
          friendId: state.id,
          source: 'request',
          userId: args.inviterId,
        }),
        ...cardsGetCompletionAnalyticsEvents(
          inviterInventoryBefore,
          inviterInventoryAfter,
          args.inviterId,
        ),
      );

      api.postMessage.cardsSendCard(args.inviterId, {
        cardId: grantedCardId,
      });

      api.scheduledActions.schedule.sendCardReceivedOA({
        args: { cardId: grantedCardId },
        delayInMS: 0,
        targetUserId: args.inviterId,
        notificationId: `cardsSendCard_${grantedCardId}`,
      });

      if (!isAlreadyFriend) {
        addFriend(state, args.inviterId, now);

        // The link redeemer is the receiver of the original card request.
        events.push({
          eventType: 'FriendAdd',
          eventProperties: {
            friendCount: getBuruFriendsCount(state),
            method: requestMethod,
            originFeature: 'cards',
            originSubFeature: cardsRequestSubFeatureByMethod[requestMethod],
            friendRequestReceiverID: state.id,
            friendRequestSenderID: args.inviterId,
            isReceiver: true,
          },
        });
      }

      if (!inviterAlreadyFriend) {
        api.postMessage.addFriendFromInvite(args.inviterId, {
          friendId: state.id,
        });

        // Emit the offline inviter side with its projected post-add count.
        events.push({
          userId: args.inviterId,
          eventType: 'FriendAdd',
          eventProperties: {
            friendCount: getBuruFriendsCount(inviterState) + 1,
            method: requestMethod,
            originFeature: 'cards',
            originSubFeature: cardsRequestSubFeatureByMethod[requestMethod],
            friendRequestReceiverID: state.id,
            friendRequestSenderID: args.inviterId,
            isReceiver: false,
          },
        });
      }

      // Include any local completion caused by invite grants before sending.
      events.push(
        ...cardsGetCompletionAnalyticsEvents(
          inviteeInventoryBefore,
          cardsGetAnalyticsInventory(state),
        ),
      );
      sendAnalyticsEvents(api, events);

      return {
        granted: true,
        grantedCardId,
      };
    },
  ),

  /**
   * Claim a card offered through a donate share link.
   *
   * The sender is authoritative for losing the duplicate card. This action
   * posts a sender-side claim message, flushes it, verifies the sender's owned
   * count decreased, then records the received card locally.
   */
  cardsClaimDonateShare: asyncAction(
    async (
      state,
      args: CardsDonateShareArgs,
      api,
    ): Promise<CardsDonateShareResult> => {
      const now = api.date.now();
      const receiverInventoryBefore = cardsGetAnalyticsInventory(state);
      const linkValidation = cardsValidateDonateShareLink(state, args);
      if (!linkValidation.valid) {
        return { granted: false, reason: linkValidation.reason };
      }

      const selfValidation = cardsValidateDonateShareSelf(state, args, now);
      if (!selfValidation.valid) {
        return { granted: false, reason: selfValidation.reason };
      }

      const senderState = await cardsFetchUserState(args.senderId, api);
      if (!senderState) {
        return { granted: false, reason: 'sender_unavailable' };
      }

      const otherValidation = cardsValidateDonateShareOther(
        senderState,
        args,
        now,
      );
      if (!otherValidation.valid) {
        return { granted: false, reason: otherValidation.reason };
      }

      const senderCardCountBeforeClaim = cardsGetOwnedCardCount(
        senderState,
        args.cardId,
        now,
      );
      api.postMessage.cardsDonateShareClaimed(args.senderId, {
        cardId: args.cardId,
      });
      await api.flushMessages();

      const updatedSenderState = await cardsFetchUserState(args.senderId, api);
      if (!updatedSenderState) {
        return { granted: false, reason: 'sender_unavailable' };
      }

      const senderCardCountAfterClaim = cardsGetOwnedCardCount(
        updatedSenderState,
        args.cardId,
        now,
      );

      if (senderCardCountAfterClaim >= senderCardCountBeforeClaim) {
        return { granted: false, reason: 'sender_unavailable' };
      }

      cardsAddCardReceived(state, {
        userId: args.senderId,
        cardId: args.cardId,
        now,
      });

      // Emit recipient-side analytics after the sender-side transfer succeeds.
      const receiverInventoryAfter = cardsGetAnalyticsInventory(state, {
        [args.cardId]: 1,
      });
      sendAnalyticsEvents(api, [
        cardsGetAcceptAnalyticsEvent(receiverInventoryAfter, {
          cardId: args.cardId,
          friendId: args.senderId,
          source: 'donate',
        }),
        ...cardsGetCompletionAnalyticsEvents(
          receiverInventoryBefore,
          receiverInventoryAfter,
        ),
      ]);

      return {
        granted: true,
        senderId: args.senderId,
        cardId: args.cardId,
      };
    },
  ),

  /**
   * Preview whether a trade share link can proceed before the opener accepts
   * and picks a counter card.
   */
  cardsPreviewTradeShareLink: asyncAction(
    async (
      state,
      args: Omit<CardsTradeArgs, 'counterCardId'>,
      api,
    ): Promise<CardsShareTradePreviewResult> => {
      const now = api.date.now();
      const linkValidation = cardsValidateTradeLink(state, args);
      if (!linkValidation.valid) {
        return { granted: false, reason: linkValidation.reason };
      }

      // Return escrowed cards from expired trades before validating the opener.
      cardsCleanupExpiredTradesWithAnalytics(state, now, api);

      // Reopened links must not offer an already-started trade again.
      const existingTrade = args.tradeId
        ? cardsGetPendingTrade(state, args.tradeId, now)
        : undefined;
      if (existingTrade) {
        return { granted: false, reason: 'trade_already_started' };
      }

      const selfValidation = cardsValidateTradeSelf(state, args, now);
      if (!selfValidation.valid) {
        return { granted: false, reason: selfValidation.reason };
      }

      const senderState = await cardsFetchUserState(args.senderId, api);
      if (!senderState) {
        return { granted: false, reason: 'sender_unavailable' };
      }

      const otherValidation = cardsValidateTradeOther(senderState, args, now);
      if (!otherValidation.valid) {
        return { granted: false, reason: otherValidation.reason };
      }

      return {
        granted: true,
        senderId: args.senderId,
        offeredCardId: args.offeredCardId,
      };
    },
  ),

  /**
   * Offer a counter card through a trade share link.
   *
   * The sender's duplicate card is escrowed first through a sender-side
   * message. Once verified, this stores the opener-side mirror as a countered
   * trade so the sender can confirm or reject from the existing trade inbox.
   */
  cardsCreateTradeFromShareLink: asyncAction(
    async (
      state,
      args: CardsTradeArgs & { counterCardId: CardId },
      api,
    ): Promise<CardsShareTradeResult> => {
      const now = api.date.now();
      const linkValidation = cardsValidateTradeLink(state, args);
      if (!linkValidation.valid) {
        return { granted: false, reason: linkValidation.reason };
      }

      // Return escrowed cards from expired trades before validating the opener.
      cardsCleanupExpiredTradesWithAnalytics(state, now, api);

      // A pending trade under this stable id means an earlier submission won.
      const existingTrade = args.tradeId
        ? cardsGetPendingTrade(state, args.tradeId, now)
        : undefined;
      if (existingTrade) {
        return {
          granted: true,
          senderId: existingTrade.proposerId,
          tradeId: existingTrade.tradeId,
        };
      }

      const selfValidation = cardsValidateTradeSelf(state, args, now);
      if (!selfValidation.valid) {
        return { granted: false, reason: selfValidation.reason };
      }

      const senderState = await cardsFetchUserState(args.senderId, api);
      if (!senderState) {
        return { granted: false, reason: 'sender_unavailable' };
      }
      const otherValidation = cardsValidateTradeOther(senderState, args, now);
      if (!otherValidation.valid) {
        return { granted: false, reason: otherValidation.reason };
      }

      const tradeId = args.tradeId ?? cardsCreateShareTradeId(state, now);
      api.postMessage.cardsTradeShareCountered(args.senderId, {
        tradeId,
        offeredCardId: args.offeredCardId,
        counterCardId: args.counterCardId,
        recipientName: state.publicProfile?.name,
        recipientAvatar: state.publicProfile?.photo,
      });
      await api.flushMessages();

      const updatedSenderState = await cardsFetchUserState(args.senderId, api);
      if (!updatedSenderState) {
        return { granted: false, reason: 'sender_unavailable' };
      }

      const senderTrade = cardsGetPendingTrade(
        updatedSenderState,
        tradeId,
        now,
      );
      if (
        !senderTrade ||
        senderTrade.proposerId !== args.senderId ||
        senderTrade.recipientId !== state.id ||
        senderTrade.status !== 'countered' ||
        senderTrade.offeredCardIds[0] !== args.offeredCardId ||
        senderTrade.counterCardIds?.[0] !== args.counterCardId
      ) {
        return { granted: false, reason: 'sender_unavailable' };
      }

      const trade = cardsAddCounteredShareTradeAsRecipient(state, {
        trade: senderTrade,
        otherUserName: updatedSenderState.publicProfile?.name,
        otherUserAvatar: updatedSenderState.publicProfile?.photo,
        now,
      });

      if (!trade) {
        // Matching mirrors created concurrently are an idempotent success.
        const hasConcurrentMatchingTrade = cardsIsValidPendingTrade(
          updatedSenderState,
          state,
          tradeId,
          now,
        );
        if (hasConcurrentMatchingTrade) {
          return {
            granted: true,
            senderId: args.senderId,
            tradeId,
          };
        }

        // Only cancel the sender when no matching recipient mirror exists.
        api.postMessage.cardsTradeCancelled(args.senderId, { tradeId });
        return { granted: false, reason: 'trade_unavailable' };
      }

      return {
        granted: true,
        senderId: args.senderId,
        tradeId,
      };
    },
  ),

  /**
   * Start an async card trade by escrowing one duplicate card.
   * Escrow means the offered card is removed from usable inventory while the
   * trade is pending, so it cannot be sent or traded elsewhere unless the trade
   * is cancelled or expires.
   * The recipient gets a `cardsTradeProposed` message and can answer with
   * `cardsCounterTrade`.
   *
   * @returns The new trade id.
   * @throws When the card is unknown, the target is self, or trade
   * limits/inventory checks fail.
   *
   * @example
   * await StateObserver.invoke.cardsProposeTrade({
   *   cardId: 'burumoments_beach_01',
   *   userId: candidateUserId,
   * });
   */
  cardsProposeTrade: action(
    (
      state,
      args: {
        cardId: CardId;
        userId: string;
        otherUserName?: string;
        otherUserAvatar?: string;
      },
      api,
    ) => {
      if (!cardsIsCardValid(args.cardId)) {
        throw new Error(`Unknown card ${args.cardId}.`);
      }

      if (args.userId === state.id) {
        throw new Error('Cannot trade a card with yourself');
      }

      const now = api.date.now();
      cardsCleanupExpiredTradesWithAnalytics(state, now, api);
      const trade = cardsCreateTrade(state, {
        recipientId: args.userId,
        otherUserName: args.otherUserName,
        otherUserAvatar: args.otherUserAvatar,
        cardId: args.cardId,
        now,
      });

      // Direct proposals have a recipient now, but the recipient chooses the counter card later.
      sendAnalyticsEvents(api, [
        cardsGetTradeSendAnalyticsEvent(state, {
          tradeId: trade.tradeId,
          offerCardId: args.cardId,
          friendId: args.userId,
        }),
      ]);

      api.postMessage.cardsTradeProposed(args.userId, {
        trade: {
          ...trade,
          otherUserName: state.publicProfile?.name,
          otherUserAvatar: state.publicProfile?.photo,
        },
      });

      return trade.tradeId;
    },
  ),

  /**
   * Accept a proposed trade by escrowing the recipient's counter-offer card.
   * Escrow removes the counter card from the recipient's usable inventory while
   * the trade is pending. The recipient-side trade is marked as `countered`,
   * and a counter message updates the proposer-side mirror. The proposer must
   * still call `cardsConfirmTrade` to complete the exchange.
   *
   * @returns True when the counter is posted.
   * @throws When the card is unknown or modifier-level trade validation fails.
   */
  cardsCounterTrade: asyncAction(
    async (state, args: { tradeId: string; cardId: CardId }, api) => {
      if (!cardsIsCardValid(args.cardId)) {
        throw new Error(`Unknown card ${args.cardId}.`);
      }

      const trade = cardsCounterTrade(state, {
        tradeId: args.tradeId,
        cardId: args.cardId,
        now: api.date.now(),
      });

      api.postMessage.cardsTradeCountered(trade.proposerId, {
        tradeId: trade.tradeId,
        counterCardIds: [args.cardId],
        recipientConfirmedAt: trade.recipientConfirmedAt ?? api.date.now(),
        updatedAt: trade.updatedAt,
      });

      // The counter is now stored locally and will update the proposer through the message.
      return true;
    },
  ),

  /**
   * Complete a countered trade as the original proposer.
   * The recipient is authoritative for the first completion step, so this
   * action posts completion, flushes the message, re-fetches the recipient, and
   * only grants the counter card locally after the recipient completion marker
   * is visible. Before completion is posted, missing recipient state or an
   * invalid matching trade cancels the proposer-side trade and releases escrow.
   *
   * @returns True when the proposer completes the trade, otherwise false.
   */
  cardsConfirmTrade: asyncAction(
    async (state, args: { tradeId: string }, api) => {
      const now = api.date.now();
      const trade = cardsGetPendingTrade(state, args.tradeId, now);

      if (
        !trade ||
        trade.proposerId !== state.id ||
        trade.status !== 'countered' ||
        !trade.counterCardIds?.length
      ) {
        return false;
      }

      const proposerInventoryBefore = cardsGetAnalyticsInventory(state);

      const statesMap = await api.fetchStates([trade.recipientId]);
      const recipientState = statesMap[trade.recipientId]?.state;
      if (!recipientState) {
        cardsCancelTrade(state, trade.tradeId);
        api.postMessage.cardsTradeCancelled(trade.recipientId, {
          tradeId: trade.tradeId,
        });
        return false;
      }

      const recipientInventoryBefore =
        cardsGetAnalyticsInventory(recipientState);

      let completedRecipientState = recipientState;
      if (!cardsHasRecipientCompletedTrade(recipientState, trade)) {
        if (
          !cardsIsValidPendingTrade(state, recipientState, trade.tradeId, now)
        ) {
          cardsCancelTrade(state, trade.tradeId);
          api.postMessage.cardsTradeCancelled(trade.recipientId, {
            tradeId: trade.tradeId,
          });
          return false;
        }

        api.postMessage.cardsTradeCompleted(trade.recipientId, {
          tradeId: trade.tradeId,
          completedAt: now,
        });
        await api.flushMessages();

        const completedStatesMap = await api.fetchStates([trade.recipientId]);
        completedRecipientState = completedStatesMap[trade.recipientId]?.state;
        if (!completedRecipientState) return false;

        if (!cardsHasRecipientCompletedTrade(completedRecipientState, trade)) {
          cardsCancelTrade(state, trade.tradeId);
          api.postMessage.cardsTradeCancelled(trade.recipientId, {
            tradeId: trade.tradeId,
          });
          return false;
        }
      }

      const completedTrade = cardsCompleteTradeAsProposer(
        state,
        trade.tradeId,
        now,
      );
      if (!completedTrade) return false;

      const offerCardId = completedTrade.offeredCardIds[0] as
        | CardId
        | undefined;
      const receiveCardId = completedTrade.counterCardIds?.[0] as
        | CardId
        | undefined;
      if (!offerCardId || !receiveCardId) return false;

      // Build effective post-trade inventories while cards remain in inboxes.
      const proposerInventoryAfter = cardsGetAnalyticsInventory(state, {
        [receiveCardId]: 1,
      });
      const recipientInventoryAfter = cardsGetAnalyticsInventory(
        completedRecipientState,
        { [offerCardId]: 1 },
      );
      sendAnalyticsEvents(api, [
        cardsGetTradeConfirmAnalyticsEvent(proposerInventoryAfter, {
          tradeId: completedTrade.tradeId,
          offerCardId,
          receiveCardId,
          friendId: completedTrade.recipientId,
        }),
        cardsGetAcceptAnalyticsEvent(proposerInventoryAfter, {
          cardId: receiveCardId,
          friendId: completedTrade.recipientId,
          source: 'trade',
        }),
        ...cardsGetCompletionAnalyticsEvents(
          proposerInventoryBefore,
          proposerInventoryAfter,
        ),
        cardsGetTradeConfirmAnalyticsEvent(recipientInventoryAfter, {
          tradeId: completedTrade.tradeId,
          offerCardId: receiveCardId,
          receiveCardId: offerCardId,
          friendId: completedTrade.proposerId,
          userId: completedTrade.recipientId,
        }),
        cardsGetAcceptAnalyticsEvent(recipientInventoryAfter, {
          cardId: offerCardId,
          friendId: completedTrade.proposerId,
          source: 'trade',
          userId: completedTrade.recipientId,
        }),
        ...cardsGetCompletionAnalyticsEvents(
          recipientInventoryBefore,
          recipientInventoryAfter,
          completedTrade.recipientId,
        ),
      ]);

      return true;
    },
  ),

  /**
   * Cancel a cancellable pending trade from either participant and return any
   * escrowed card owned by the caller. The other participant receives
   * `cardsTradeCancelled`.
   *
   * @returns True when a cancellable trade was found and cancelled, otherwise
   * false.
   */
  cardsCancelTrade: asyncAction(
    async (state, args: { tradeId: string }, api) => {
      const trade = cardsCancelTrade(state, args.tradeId);
      if (!trade) return false;

      const targetId =
        trade.proposerId === state.id ? trade.recipientId : trade.proposerId;
      api.postMessage.cardsTradeCancelled(targetId, { tradeId: args.tradeId });

      // Proposers cancel; recipients reject. Both terminate the proposer funnel.
      const offerCardId = trade.offeredCardIds[0] as CardId | undefined;
      if (!offerCardId) return true;

      if (trade.proposerId === state.id) {
        sendAnalyticsEvents(api, [
          cardsGetTradeRejectAnalyticsEvent(state, {
            tradeId: trade.tradeId,
            offerCardId,
            friendId: trade.recipientId,
            reason: 'cancelled',
          }),
        ]);
        return true;
      }

      // Flush the cancellation so proposer context includes returned escrow.
      await api.flushMessages();
      const proposerState = await cardsFetchUserState(trade.proposerId, api);
      if (proposerState) {
        sendAnalyticsEvents(api, [
          cardsGetTradeRejectAnalyticsEvent(proposerState, {
            tradeId: trade.tradeId,
            offerCardId,
            friendId: trade.recipientId,
            reason: 'rejected',
            userId: trade.proposerId,
          }),
        ]);
      }

      return true;
    },
  ),
});
